import { ObjectId } from '@racemap/sdk/schema/base';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { isValidEmail } from '@racemap/utilities/functions/validation';
import { Button, Input, Tooltip } from 'antd';
import { type ComponentProps, type FC, type ReactNode, useEffect, useState } from 'react';
import { IconFail, IconOk, IconPlus, IconRemove } from '../Icons';

interface Props {
  value: ObjectId | null;
  onChange: (value: ObjectId | null) => void;
  disabled?: boolean;
  placeholder?: string;
  allowClear?: boolean;
  action?: 'add' | 'remove' | 'search';
}

const { Search } = Input;
const apiClient = RacemapAPIClient.fromWindowLocation();

export const UserPerEmailFetcher: FC<Props> = ({
  value,
  onChange,
  disabled,
  placeholder,
  allowClear,
  action = 'search',
}) => {
  const [userEmail, setUserEmail] = useState<string>('');
  const [state, setState] = useState<'error' | 'success'>();
  const [stateInfo, setStateInfo] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const loadUser = async ({
    email,
    userId,
  }: { email?: string; userId?: ObjectId }): Promise<void> => {
    try {
      if ((email == null || email === '') && userId == null) return;
      if (email != null && !isValidEmail(userEmail)) throw new Error('Invalid email');
      if (userId != null && !ObjectId.isValid(userId)) throw new Error('Invalid id');

      setIsLoading(true);
      const user = await apiClient.userLookup({ email, userId: userId?.toHexString() });
      if (user == null) throw new Error('User not found');

      setState('success');
      setStateInfo('User found');
      setUserEmail(user.email);

      onChange(user.id);
      setIsLoading(false);

      setTimeout(() => {
        setState(undefined);
        setStateInfo('');
      }, 3000);
    } catch (error) {
      if (error instanceof HTTPFetchError || error instanceof Error) {
        if (('status' in error && error.status === 404) || error.message === 'User not found') {
          setStateInfo('User not found');
        } else {
          setStateInfo(error.message || 'Error occurred');
        }
        console.error(error);
        setIsLoading(false);
        setState('error');

        setTimeout(() => {
          setState(undefined);
          setStateInfo('');
        }, 10000);
      }
    }
  };

  const handleSearch: ComponentProps<typeof Search>['onSearch'] = async (_v, _e, options) => {
    if (options?.source === 'clear') return;
    loadUser({ email: userEmail });
  };

  useEffect(() => {
    if (value == null || userEmail !== '') return;
    loadUser({ userId: value });
  }, [value]);

  return (
    <Search
      value={userEmail}
      onChange={({ target }) => setUserEmail(target.value)}
      onSearch={handleSearch}
      loading={isLoading}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled}
      enterButton={getEnterButton(action)}
      status={state === 'error' ? 'error' : undefined}
      prefix={getPrefix(state, stateInfo)}
    />
  );
};

const getEnterButton = (action: 'add' | 'remove' | 'search') => {
  switch (action) {
    case 'add':
      return <Button icon={<IconPlus />} />;
    case 'remove':
      return <Button icon={<IconRemove />} />;
    case 'search':
      return false;
  }
};

const getPrefix = (state: undefined | 'error' | 'success', stateInfo: string) => {
  let icon: ReactNode | null = null;
  switch (state) {
    case 'error':
      icon = <IconFail color={RacemapColors.BaseRed} />;
      break;
    case 'success':
      icon = <IconOk color={RacemapColors.BaseGreen} />;
      break;
  }

  if (icon == null) return null;

  return <Tooltip title={stateInfo}>{icon}</Tooltip>;
};
